<template>
    <svg
        viewBox="0 0 36 36"
        class="dashed-circle-spinner"
        :class="props.rootClass"
        :style="rootStyle"
        aria-hidden="true"
    >
        <circle
            cx="18"
            cy="18"
            :r="circleRadius"
            fill="none"
            :stroke="props.strokeColor"
            :stroke-width="props.strokeWidth"
            stroke-linecap="round"
            :stroke-dasharray="props.strokeDasharray"
        />
    </svg>
</template>

<script setup lang="ts">
import type { StyleValue } from 'vue'

/**
 * Decorative indeterminate spinner: gold dashed ring that rotates continuously.
 *
 * Used on Processing / loading scrims beside status copy. Purely visual (`aria-hidden`);
 * parent regions should expose `role="status"` and an accessible label when this indicates
 * loading state.
 *
 * **Nuxt registration:** `components/shared/DashedCircleSpinner.vue` → `SharedDashedCircleSpinner`.
 */
const props = withDefaults(
    defineProps<{
        /** Rendered width and height in CSS pixels (viewBox stays 36×36). */
        size?: number
        /** SVG stroke color (defaults to brand gold). */
        strokeColor?: string
        /** Circle stroke width in user units. */
        strokeWidth?: number
        /** `stroke-dasharray` for the dashed ring. */
        strokeDasharray?: string
        /** Extra classes merged onto the root `<svg>`. */
        rootClass?: string | string[] | Record<string, boolean>
    }>(),
    {
        size: 36,
        strokeColor: 'var(--gold)',
        strokeWidth: 2,
        strokeDasharray: '3 5',
        rootClass: undefined,
    }
)

/** Circle radius in viewBox units (center 18,18). */
const circleRadius = 15

/** Root dimensions for the `<svg>`. */
const rootStyle = computed<StyleValue>(() => ({
    width: `${props.size}px`,
    height: `${props.size}px`,
}))
</script>

<style scoped>
.dashed-circle-spinner {
    flex: 0 0 auto;
    animation: dashed-circle-spinner-spin 3.2s linear infinite;
}

@keyframes dashed-circle-spinner-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .dashed-circle-spinner {
        animation: none;
    }
}
</style>
